import { AutoComplete, Checkbox, DatePicker, Input, Select, TimePicker, Typography } from "antd";
import styled, { css } from "styled-components";

import { Palette } from "../../core/styles/theme";

//------------------------------------------------------------------------------------------------
// --------Define CSS rules------------------------------------------------------------------------
//------------------------------------------------------------------------------------------------
const inputStyles = css`
    &.aevi-input {
        font-family: "RobotoLight" !important;
        font-size: 12pt;
        font-weight: 300;
        line-height: 18pt;
        color: #3e383a; // Create colors in Pallette?
        border: 1pt solid ${Palette.MidGray};
        border-radius: 4pt;
        padding: 6pt 10pt;
        min-width: 15ch;
        height: 40px;
        width: 100%;

        &:hover {
            border-color: ${Palette.DarkGreen};
        }
    }

    &.aevi-input-disabled {
        color: ${Palette.DisabledColor} !important;
    }

    &.aevi-input-text {
    }

    &.aevi-input-number {
        // Hide the native spinner: with very large values it's unusable, and scrolling
        // over a focused field can silently change the value via the hidden buttons.
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }

    &.aevi-input-textarea {
        min-height: 80px;
    }

    &.aevi-input-textarea-nowrap {
        min-height: 80px;
        white-space: pre;
        overflow-x: auto;
        resize: both;
        max-width: none;
    }

    &.aevi-input-checkbox {
        font-size: 12pt;
        margin: 0 !important;
    }

    &.aevi-input-checkbox-marked {
    }

    &.aevi-checkbox-disabled {
        color: ${Palette.DisabledColor} !important;
        height: 18pt;
        width: 18pt;
    }

    &.aevi-input-select {
        min-width: 15ch;
        min-height: 40px;
    }

    &.aevi-select-disabled {
        color: ${Palette.DisabledColor} !important;
        background: ${Palette.DisabledBackground};
        cursor: not-allowed;
    }
`;

// antd sizes the Select clear icon with fontSizeIcon (12px), while the DatePicker/TimePicker
// clear icon inherits the 16px input font size. Match them so the icon is consistent across form fields.
const selectClearIconStyles = css`
    &.ant-select-allow-clear .ant-select-clear {
        width: 16px;
        height: 16px;
        margin-top: -8px;
        font-size: 16px;
    }
`;

//------------------------------------------------------------------------------------------------
// --------Define styles---------------------------------------------------------------------------
//------------------------------------------------------------------------------------------------
const InputHtmlStyle = styled.input`
    ${inputStyles};
`;

const InputAntStyle = styled(Input)`
    ${inputStyles};
`;

// antd places the picker clear icon on top of the calendar/clock suffix and fades the suffix out on hover.
// Keep the suffix on the far right and show the clear icon next to it, so the user can't clear
// the value by accident when aiming for the suffix (AP3ADMIN-5023).
// Offset = suffix icon width (1em, same font size as the clear icon) + antd affix gap (marginXS, 8px)
export const pickerClearIconStyles = css`
    &.ant-picker:hover .ant-picker-suffix:not(:last-child) {
        opacity: 1;
    }

    &.ant-picker:not(.ant-picker-range):not(.ant-picker-multiple) .ant-picker-clear {
        inset-inline-end: calc(1em + 8px);
    }
`;

const DatePickerAntStyle = styled(DatePicker)`
    ${inputStyles};
    ${pickerClearIconStyles};
`;

const TimePickerAntStyle = styled(TimePicker)`
    ${pickerClearIconStyles};
`;

const InputNumberAntStyle = styled(Input)`
    ${inputStyles};
`;

const InputPasswordAntStyle = styled(Input.Password)`
    ${inputStyles};
`;

const TextareaInputAntStyle = styled(Input.TextArea)`
    ${inputStyles};
`;
//------------------------------------------------------------------------------------------------
// --------Export components-----------------------------------------------------------------------
//------------------------------------------------------------------------------------------------
export const InputTextHtmlStyle = styled(InputHtmlStyle).attrs(() => ({ className: "aevi-input aevi-input-text" as string }))``;
export const InputTextStyle = styled(InputAntStyle).attrs(() => ({ className: "aevi-input aevi-input-text" as string }))``;
export const InputNumberStyle = styled(InputNumberAntStyle).attrs(() => ({ className: "aevi-input aevi-input-number" as string }))``;
export const InputPasswordStyle = styled(InputPasswordAntStyle).attrs(() => ({ className: "aevi-input aevi-input-text" as string }))``;
export const TextareaInputStyle = styled(TextareaInputAntStyle).attrs(() => ({ className: "aevi-input aevi-input-textarea" as string }))``;
export const InputDatePickerStyle = styled(DatePickerAntStyle).attrs(() => ({ className: "aevi-input aevi-input-text" as string }))``;
export const InputTimePickerStyle = styled(TimePickerAntStyle).attrs(() => ({ className: "aevi-input aevi-input-text" as string }))``;

//------------------------------------------------------------------------------------------------
//------------------------------------------------------------------------------------------------
//------------------------------------------------------------------------------------------------
//------------------------------------------------------------------------------------------------

const SelectInputHtmlStyles = styled.select`
    ${inputStyles};
`;

const SelectInputAntStyles = styled(Select)`
    ${inputStyles};
    // stretching the height when a multiselect has many options and goes to multiple lines
    ${(props) => {
        if (props.mode === "multiple") {
            return css`
                height: auto !important;
                &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
                    margin: -3px 0 !important;
                }
            `;
        }
        return css`
            height: 40px;
        `;
    }}}

    &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
        border: 0;
        padding: 0;
    }

    &.ant-select-status-error {
        border-color: var(--ant-color-error);
    }

    &.ant-select-status-error:hover {
        border-color: var(--ant-color-error);
    }

    &.ant-select-focused.ant-select:not(.ant-select-disabled):not(.ant-select-customize-input):not(.ant-pagination-size-changer)
        .ant-select-selector {
        box-shadow: none;
    }

    &.ant-select-single .ant-select-selection-item {
        font-size: 12pt;
    }

    &.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
        background: transparent;
        cursor: not-allowed;
    }

    &.ant-select .ant-select-arrow {
        margin-top: -5px;
        font-size: 14px;
        color: ${Palette.AeviBlack};
    }

    // Keep the arrow on the far right and show the clear icon next to it, so the user can't clear
    // the value by accident when aiming for the arrow
    // Offset = root padding (10pt) + suffix icon width (fontSizeIcon, 12px) + antd affix gap (marginXS, 8px)
    &.ant-select-show-arrow .ant-select-clear {
        inset-inline-end: calc(10pt + 12px + 8px);
    }

    ${selectClearIconStyles};
`;

export const SelectInputHtmlStyle = styled(SelectInputHtmlStyles).attrs(() => ({ className: "aevi-input aevi-input-select" as string }))``;
export const SelectInputStyle = styled(SelectInputAntStyles).attrs(() => ({ className: "aevi-input aevi-input-select" as string }))``;
export const SelectInputMultiStyle = styled(SelectInputStyle).attrs(() => ({
    className: "aevi-input aevi-input-select" as string,
    mode: "multiple" as "multiple" | "tags",
}))``;

export const AutoCompleteStyle = styled(AutoComplete<string>)`
    ${selectClearIconStyles};
`;

const CheckboxInputAntStyles = styled(Checkbox)`
    ${inputStyles};

    &.ant-checkbox-wrapper {
        align-items: center;
    }

    & .ant-checkbox .ant-checkbox-inner {
        height: 18pt;
        width: 18pt;
        border-color: ${Palette.HeroGreen} !important;
    }

    & .ant-checkbox-checked .ant-checkbox-inner {
        background-color: ${Palette.HeroGreen} !important;
        border-color: ${Palette.HeroGreen} !important;
        height: 18pt;
        width: 18pt;
    }

    & .ant-checkbox-checked .ant-checkbox-inner:after {
        transform: rotate(40deg) scale(1.5) translate(-0%, -50%) !important;
    }

    & .ant-checkbox-disabled .ant-checkbox-inner {
        border-color: ${Palette.DisabledBackground} !important;
        background-color: ${Palette.DisabledBackground} !important;
    }

    & .ant-checkbox-label {
        font-size: 12px;
        color: ${Palette.FormText};
    }
`;
export const CheckboxInputStyle = styled(CheckboxInputAntStyles).attrs(() => ({ className: "aevi-input-checkbox" as string }))``;

// Small secondary text shown below a field to give guidance (e.g. expected format, available shortcuts).
export const HelperText = styled(Typography.Text)`
    font-size: 12px;
    margin-top: 4px;
    display: block;
`;
